import { createApi } from "@reduxjs/toolkit/query/react";
import { reduce } from "lodash";

import { baseQuery } from "@tsp-core/store/utils";

import { ContractTypeId } from "@tsp-domains/contract-types/types";
import { parseContractTypeId } from "@tsp-domains/contract-types/utils";
import { CostAllocationPeriod } from "@tsp-domains/cost-allocation/enums";
import { DepartmentUuid } from "@tsp-domains/departments/types";
import { parseDepartmentUuid } from "@tsp-domains/departments/utils";
import { GeneralLedgerAccountCode } from "@tsp-domains/general-ledger-accounts/types";
import { parseGeneralLedgerAccountCode } from "@tsp-domains/general-ledger-accounts/utils";

import { WorkTypeStatus } from "../enums";
import { WorkTypeDepartmentBreakdown, WorkTypeId, WorkTypesDictionary } from "../types";

type GetWorkTypesResponseData = {
  items: Array<{
    id: WorkTypeId;
    name: string;
    description: string | null;
    status: WorkTypeStatus;
    contractTypeId: ContractTypeId;
    contractTypeName: string;
    defaultCostAllocationPeriodType: CostAllocationPeriod;
    hasActiveRates: boolean;
    isForAllDepartments: boolean;
    departmentMappings: Record<DepartmentUuid, GeneralLedgerAccountCode>;
  }>;
};

export const workTypesApi = createApi({
  reducerPath: "@work-types/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 60 * 60 * 24,
  tagTypes: ["WorkTypesDictionary"],
  endpoints: (builder) => ({
    getWorkTypesDictionary: builder.query<WorkTypesDictionary, void>({
      query: () => ({
        url: "/workTypes",
        method: "POST",
      }),
      providesTags: ["WorkTypesDictionary"],
      transformResponse: (data: GetWorkTypesResponseData) => {
        return data.items.reduce((accumulator, item) => {
          accumulator[item.id] = {
            id: item.id,
            name: item.name,
            description: item.description,
            status: item.status,
            contractTypeId: parseContractTypeId(item.contractTypeId),
            defaultCostAllocationPeriodType: item.defaultCostAllocationPeriodType,
            isForAllDepartments: item.isForAllDepartments,
            departmentBreakdowns: reduce(
              item.departmentMappings,
              (accumulator, generalLedgerAccountCode, departmentUuid: DepartmentUuid) => {
                accumulator.push({
                  departmentUuid,
                  generalLedgerAccountCode: parseGeneralLedgerAccountCode(generalLedgerAccountCode),
                });

                return accumulator;
              },
              [] as Array<WorkTypeDepartmentBreakdown>,
            ),
          };
          return accumulator;
        }, {} as WorkTypesDictionary);
      },
    }),
  }),
});
